import React from "react";
import { cn } from "@/lib/utils";

/**
 * PageHeader — page-level title block.
 *
 * Composition:
 *   • eyebrow    — small uppercase kicker (e.g. section label)
 *   • title      — h1 default (override via `as`)
 *   • description— sub-title paragraph
 *   • actions    — right-aligned buttons/controls
 *   • breadcrumbs— optional slot rendered above the title
 *
 * The container is intentionally flat — no shadow, no card, no
 * hero gradient. Executive layout = airy whitespace + hard-left
 * alignment.
 */

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  breadcrumbs,
  as: Tag = "h1",
  className,
  "data-testid": testId,
}) {
  return (
    <header
      data-testid={testId}
      className={cn(
        "flex flex-col gap-4 border-b border-[var(--ds-color-border)] pb-6 mb-6",
        className
      )}
    >
      {breadcrumbs && (
        <nav aria-label="Breadcrumb" className="text-[13px] text-[var(--ds-color-ink-muted)]">
          {breadcrumbs}
        </nav>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-color-ink-subtle)]">
              {eyebrow}
            </div>
          )}
          {title && (
            <Tag
              className={cn(
                "mt-1 text-[28px] leading-tight font-semibold text-[var(--ds-color-ink)]",
                "font-[var(--ds-font-display)]"
              )}
              style={{ letterSpacing: "-0.02em" }}
            >
              {title}
            </Tag>
          )}
          {description && (
            <p className="mt-2 max-w-2xl text-[14px] leading-snug text-[var(--ds-color-ink-muted)]">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export default PageHeader;
